// frontend/src/components/LLMSelector.tsx
// ENHANCED VERSION - ALL EXISTING CODE PRESERVED + BACKEND INTEGRATION ADDED

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Cpu, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
// NEW ADDITION: Import the enhanced API functions
import { getAvailableAIModels, setAIModel, getCurrentAIModel } from '../api';

// EXISTING INTERFACE - ENHANCED
interface LLMModel {
  id: string;
  name: string;
  description: string;
  badge?: string;
  // NEW ADDITIONS: Backend integration fields
  provider?: string;
  available?: boolean;
  features?: string[];
}

// EXISTING INTERFACE - UNCHANGED
interface LLMSelectorProps {
  onModelChange?: (modelId: string, modelName: string) => void;
  defaultModel?: string;
  className?: string;
  // NEW ADDITION: Optional props for enhanced functionality
  showStatus?: boolean;
}

const LLMSelector: React.FC<LLMSelectorProps> = ({ 
  onModelChange, 
  defaultModel = 'Claude 4 Sonnet',
  className = '',
  showStatus = true // NEW: Show availability status
}) => {
  // EXISTING STATE - UNCHANGED
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLLM, setSelectedLLM] = useState(defaultModel);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // NEW STATE: Backend integration
  const [availableModels, setAvailableModels] = useState<LLMModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [backendConnected, setBackendConnected] = useState(false);

  // EXISTING STATIC MODELS - PRESERVED AS FALLBACK
  const staticLlmModels: LLMModel[] = [
    { 
      id: 'claude-4-sonnet', 
      name: 'Claude 4 Sonnet', 
      description: 'Best overall performance',
      badge: 'Recommended',
      provider: 'Anthropic',
      available: true,
      features: ['text_analysis', 'vision', 'document_processing']
    },
    { 
      id: 'claude-3.5-haiku', 
      name: 'Claude 3.5 Haiku', 
      description: 'Fastest response times',
      badge: 'Fast',
      provider: 'Anthropic',
      available: true,
      features: ['text_analysis', 'fast_processing']
    },
    { 
      id: 'claude-3-opus', 
      name: 'Claude 3 Opus', 
      description: 'Most capable model',
      badge: 'Advanced',
      provider: 'Anthropic',
      available: true,
      features: ['text_analysis', 'advanced_reasoning']
    },
    { 
      id: 'gpt-4', 
      name: 'GPT-4', 
      description: 'OpenAI flagship model',
      provider: 'OpenAI',
      available: false, // Will be updated from backend
      features: ['text_analysis', 'reasoning']
    },
    { 
      id: 'gpt-4-turbo', 
      name: 'GPT-4 Turbo', 
      description: 'Faster GPT-4 variant',
      provider: 'OpenAI',
      available: false, // Will be updated from backend
      features: ['text_analysis', 'fast_processing']
    },
    { 
      id: 'gemini-pro', 
      name: 'Gemini Pro', 
      description: 'Google\'s advanced model',
      provider: 'Google',
      available: false, // Will be updated from backend
      features: ['text_analysis', 'multimodal']
    }
  ];

  // NEW FUNCTION: Load available models from backend
  const loadAvailableModels = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAvailableAIModels();
      
      // Merge backend data with static models for complete information
      const mergedModels = staticLlmModels.map(staticModel => {
        const backendModel = response.models.find(m => m.id === staticModel.id);
        return {
          ...staticModel,
          ...backendModel, // Backend data overrides static data
          available: backendModel?.available ?? staticModel.available
        };
      });
      
      setAvailableModels(mergedModels);
      setBackendConnected(true);
      
      // If current selection is not available, fall back to default
      const currentModel = getCurrentAIModel();
      const isCurrentModelAvailable = mergedModels.find(
        m => m.id === currentModel.id && m.available
      );
      
      if (!isCurrentModelAvailable) {
        const defaultAvailable = mergedModels.find(
          m => m.id === response.default && m.available
        );
        if (defaultAvailable) {
          handleLLMChange(defaultAvailable);
        }
      }
      
    } catch (err) {
      console.error('Error loading AI models from backend:', err);
      setError('Failed to load AI models from backend');
      setBackendConnected(false);
      
      // Fallback to static models
      setAvailableModels(staticLlmModels);
    } finally {
      setLoading(false);
    }
  };

  // NEW EFFECT: Load models from backend on mount
  useEffect(() => {
    loadAvailableModels();
  }, []);

  // EXISTING EFFECT - UNCHANGED
  useEffect(() => {
    if (!showDropdown) return;
    
    function handleClickOutside(event: PointerEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    
    function handleResize() {
      setShowDropdown(false);
    }
    
    document.addEventListener('pointerdown', handleClickOutside, true);
    window.addEventListener('resize', handleResize, true);
    
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside, true);
      window.removeEventListener('resize', handleResize, true);
    };
  }, [showDropdown]);

  // EXISTING EFFECT - ENHANCED
  useEffect(() => {
    // Try to get from our enhanced API first
    const currentModel = getCurrentAIModel();
    if (currentModel.name) {
      setSelectedLLM(currentModel.name);
    } else {
      // Fallback to existing localStorage logic
      const savedLLM = localStorage.getItem('selectedLLM');
      if (savedLLM) {
        try {
          const parsed = JSON.parse(savedLLM);
          setSelectedLLM(parsed.name);
        } catch (error) {
          console.error('Error parsing saved LLM selection:', error);
        }
      }
    }
  }, []);

  // EXISTING FUNCTION - ENHANCED
  const handleLLMChange = (model: LLMModel) => {
    // NEW: Check if model is available
    if (!model.available) {
      console.warn('Attempted to select unavailable model:', model.name);
      return; // Don't allow selection of unavailable models
    }

    setSelectedLLM(model.name);
    setShowDropdown(false);
    
    // EXISTING LOGIC - Use enhanced API function
    setAIModel(model.id, model.name);
    
    // EXISTING CALLBACK - UNCHANGED
    if (onModelChange) {
      onModelChange(model.id, model.name);
    }
    
    // ENHANCED LOGGING
    console.log('Selected AI model:', {
      id: model.id,
      name: model.name,
      provider: model.provider,
      available: model.available,
      features: model.features
    });
  };

  // EXISTING FUNCTION - UNCHANGED
  const getBadgeColor = (badge?: string) => {
    switch (badge) {
      case 'Recommended':
        return 'bg-green-100 text-green-700';
      case 'Fast':
        return 'bg-blue-100 text-blue-700';
      case 'Advanced':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // NEW FUNCTION: Get provider-specific styling
  const getProviderColor = (provider: string, available: boolean) => {
    if (!available) return 'bg-gray-100 text-gray-500';
    
    switch (provider) {
      case 'Anthropic':
        return 'bg-green-100 text-green-700';
      case 'OpenAI':
        return 'bg-blue-100 text-blue-700';
      case 'Google':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-purple-100 text-purple-700';
    }
  };

  // NEW FUNCTION: Get status icon
  const getStatusIcon = (available: boolean) => {
    if (available) {
      return <CheckCircle className="w-3 h-3 text-green-600" />;
    } else {
      return <AlertCircle className="w-3 h-3 text-red-500" />;
    }
  };

  // NEW FUNCTION: Get selected model info
  const getSelectedModel = () => {
    return availableModels.find(model => model.name === selectedLLM);
  };

  const selectedModel = getSelectedModel();

  // NEW: Loading state
  if (loading) {
    return (
      <div className={`flex items-center space-x-2 bg-dusky-purple px-4 py-2 rounded-lg ${className}`}>
        <Loader2 className="w-4 h-4 text-electric-blue animate-spin" />
        <div className="text-left">
          <p className="text-sm font-medium text-white">Loading...</p>
          <p className="text-xs text-gray-300">AI Models</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* EXISTING BUTTON - ENHANCED */}
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center space-x-2 bg-dusky-purple hover:bg-medium-purple px-4 py-2 rounded-lg transition-colors"
        disabled={loading}
      >
        <Cpu className="w-4 h-4 text-electric-blue" />
        <div className="text-left">
          <div className="flex items-center space-x-1">
            <p className="text-sm font-medium text-white">{selectedLLM}</p>
            {/* NEW: Show status icon if enabled */}
            {showStatus && selectedModel && (
              <div className="flex items-center">
                {getStatusIcon(selectedModel.available!)}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <p className="text-xs text-gray-300">AI Model</p>
            {/* NEW: Show provider if available */}
            {selectedModel && selectedModel.provider && (
              <span className={`text-xs px-1 py-0.5 rounded ${getProviderColor(selectedModel.provider, selectedModel.available!)}`}>
                {selectedModel.provider}
              </span>
            )}
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-white" />
      </button>

      {/* EXISTING DROPDOWN - ENHANCED */}
      {showDropdown && (
        <div 
          ref={dropdownRef} 
          className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg py-2 z-50 border border-gray-200"
        >
          {/* EXISTING HEADER - ENHANCED */}
          <div className="px-4 py-2 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">Select AI Model</h3>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">Choose the AI model for analysis</p>
              {/* NEW: Backend connection status */}
              <div className="flex items-center space-x-1">
                {backendConnected ? (
                  <CheckCircle className="w-3 h-3 text-green-600" />
                ) : (
                  <AlertCircle className="w-3 h-3 text-yellow-500" />
                )}
                <span className="text-xs text-gray-400">
                  {backendConnected ? 'Live' : 'Offline'}
                </span>
              </div>
            </div>
            {/* NEW: Error display */}
            {error && (
              <div className="mt-1 flex items-center space-x-1">
                <AlertCircle className="w-3 h-3 text-red-500" />
                <p className="text-xs text-red-500">{error}</p>
                <button 
                  onClick={loadAvailableModels}
                  className="text-xs text-blue-500 hover:underline ml-1"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
          
          {/* EXISTING MODEL LIST - ENHANCED */}
          <div className="max-h-64 overflow-y-auto">
            {availableModels.map((model) => (
              <button
                key={model.id}
                onClick={() => handleLLMChange(model)}
                disabled={!model.available} // NEW: Disable unavailable models
                className={`w-full text-left px-4 py-3 transition-colors ${
                  model.available 
                    ? 'hover:bg-gray-50 cursor-pointer' 
                    : 'cursor-not-allowed opacity-60'
                } ${
                  selectedLLM === model.name ? 'bg-light-aqua' : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className={`text-sm font-medium ${model.available ? 'text-gray-900' : 'text-gray-500'}`}>
                        {model.name}
                      </p>
                      {/* EXISTING BADGE */}
                      {model.badge && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getBadgeColor(model.badge)}`}>
                          {model.badge}
                        </span>
                      )}
                      {/* NEW: Provider badge */}
                      {model.provider && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getProviderColor(model.provider, model.available!)}`}>
                          {model.provider}
                        </span>
                      )}
                      {/* NEW: Status icon */}
                      {getStatusIcon(model.available!)}
                    </div>
                    {/* EXISTING DESCRIPTION */}
                    <p className={`text-xs mt-0.5 ${model.available ? 'text-gray-500' : 'text-gray-400'}`}>
                      {model.description}
                    </p>
                    {/* NEW: Features display */}
                    {model.features && model.features.length > 0 && (
                      <div className="flex space-x-1 mt-1">
                        {model.features.slice(0, 2).map((feature, idx) => (
                          <span 
                            key={idx}
                            className={`text-xs px-1 py-0.5 rounded ${
                              model.available ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-400'
                            }`}
                          >
                            {feature.replace('_', ' ')}
                          </span>
                        ))}
                        {model.features.length > 2 && (
                          <span className={`text-xs ${model.available ? 'text-gray-500' : 'text-gray-400'}`}>
                            +{model.features.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* EXISTING SELECTION INDICATOR */}
                  {selectedLLM === model.name && model.available && (
                    <div className="text-electric-blue ml-2">
                      <Cpu className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          
          {/* EXISTING FOOTER - ENHANCED */}
          <div className="px-4 py-2 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Model selection affects analysis quality and response time
            </p>
            {/* NEW: Statistics and refresh */}
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-gray-400">
                Available: {availableModels.filter(m => m.available).length}/{availableModels.length}
              </span>
              <button 
                onClick={loadAvailableModels}
                className="text-xs text-blue-500 hover:underline"
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LLMSelector;